import React from "react";
import { BriefcaseBusiness, Info, Landmark, Wrench } from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./LenderManualLoanCaseFinancialInfo.css";
const EMPTY_APPLICANT_FINANCIAL_INFO = {
  employment_status: "",
};
const EMPTY_FINANCIAL_INFO = {
  primary_applicant: { ...EMPTY_APPLICANT_FINANCIAL_INFO },
  co_applicant: null,
  available_renovation_budget: "",
};
const EMPLOYMENT_STATUS_OPTIONS = [
  {
    value: "FULL_TIME",
    labelKey: "employmentStatuses.fullTime",
  },
  {
    value: "PART_TIME",
    labelKey: "employmentStatuses.partTime",
  },
  {
    value: "SELF_EMPLOYED",
    labelKey: "employmentStatuses.selfEmployed",
  },
  {
    value: "UNEMPLOYED",
    labelKey: "employmentStatuses.unemployed",
  },
  {
    value: "STUDENT",
    labelKey: "employmentStatuses.student",
  },
  {
    value: "RETIRED",
    labelKey: "employmentStatuses.retired",
  },
  {
    value: "DISABILITY_ALLOWANCE",
    labelKey: "employmentStatuses.disabilityAllowance",
  },
  {
    value: "OTHER",
    labelKey: "employmentStatuses.other",
  },
];
function hasValue(value) {
  return String(value ?? "").trim().length > 0;
}
function toNumber(value) {
  if (!hasValue(value)) {
    return 0;
  }
  const parsed = Number(String(value).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
}
function sanitizeMoneyInput(value = "") {
  const raw = String(value)
    .replace(/[^\d.,]/g, "")
    .replace(",", ".");
  if (!raw) {
    return "";
  }
  const firstDecimalIndex = raw.indexOf(".");
  if (firstDecimalIndex === -1) {
    return raw.replace(/\D/g, "");
  }
  const integerPart = raw.slice(0, firstDecimalIndex).replace(/\D/g, "");
  const decimalPart = raw
    .slice(firstDecimalIndex + 1)
    .replace(/\D/g, "")
    .slice(0, 2);
  if (raw.endsWith(".") && decimalPart.length === 0) {
    return `${integerPart}.`;
  }
  return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
}
function normalizeApplicantFinancialInfo(source = {}) {
  return {
    ...EMPTY_APPLICANT_FINANCIAL_INFO,
    ...(source && typeof source === "object" ? source : {}),
    employment_status: source?.employment_status || "",
  };
}
function normalizeFinancialInfo(source = {}, hasCoApplicant = false) {
  const value = source && typeof source === "object" ? source : {};
  const primarySource =
    value.primary_applicant && typeof value.primary_applicant === "object"
      ? value.primary_applicant
      : { employment_status: value.employment_status || "" };
  const coSource =
    value.co_applicant && typeof value.co_applicant === "object"
      ? value.co_applicant
      : {};
  return {
    primary_applicant: normalizeApplicantFinancialInfo(primarySource),
    co_applicant: hasCoApplicant
      ? normalizeApplicantFinancialInfo(coSource)
      : null,
    available_renovation_budget:
      value.available_renovation_budget == null
        ? ""
        : String(value.available_renovation_budget),
  };
}

function normalizeEuroInput(value = "") {
  const cleaned = String(value)
    .replace(/€/g, "")
    .replace(/,/g, "")
    .replace(/[^\d.]/g, "");
  if (!cleaned) {
    return "";
  }
  const decimalIndex = cleaned.indexOf(".");
  if (decimalIndex === -1) {
    return cleaned.replace(/^0+(?=\d)/, "");
  }
  const integerPart = cleaned
    .slice(0, decimalIndex)
    .replace(/\D/g, "")
    .replace(/^0+(?=\d)/, "");
  const decimalPart = cleaned
    .slice(decimalIndex + 1)
    .replace(/\D/g, "")
    .slice(0, 2);
  return `${integerPart || "0"}.${decimalPart}`;
}
function formatEuroInput(value = "") {
  const normalized = normalizeEuroInput(value);
  if (!normalized) {
    return "";
  }
  const hasDecimalPoint = normalized.includes(".");
  const [integerPart = "0", decimalPart = ""] = normalized.split(".");
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return hasDecimalPoint
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;
}
function FieldTooltip({
  text,
  accessibleLabel = "More information",
  forceOpen = false,
}) {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const tooltipId = React.useId();
  if (!text) {
    return null;
  }
  const isOpen = forceOpen || isHovered || isFocused;
  return (
    <span
      className={`lender-financial-step__tooltip ${
        isOpen ? "lender-financial-step__tooltip--open" : ""
      }`}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
    >
      <button
        type="button"
        className="lender-financial-step__tooltip-trigger"
        aria-label={accessibleLabel}
        aria-describedby={isOpen ? tooltipId : undefined}
        aria-expanded={isOpen}
        onFocus={() => {
          setIsFocused(true);
        }}
        onBlur={() => {
          setIsFocused(false);
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsFocused(true);
        }}
      >
        <Info size={14} strokeWidth={2.2} />
      </button>
      <span
        id={tooltipId}
        className="lender-financial-step__tooltip-content"
        role="tooltip"
        aria-hidden={!isOpen}
      >
        {text}
      </span>
    </span>
  );
}
function FieldLabel({
  label,
  htmlFor,
  required = false,
  tooltip = "",
  tooltipLabel = "More information",
  forceTooltipOpen = false,
}) {
  return (
    <div className="lender-financial-step__label-row">
      <div className="lender-financial-step__label-group">
        <label className="lender-financial-step__label" htmlFor={htmlFor}>
          {label}
          {required ? (
            <span
              className="lender-financial-step__required"
              aria-hidden="true"
            >
              *
            </span>
          ) : null}
        </label>
        {tooltip ? (
          <FieldTooltip
            text={tooltip}
            accessibleLabel={tooltipLabel}
            forceOpen={forceTooltipOpen}
          />
        ) : null}
      </div>
    </div>
  );
}
function EmploymentStatusField({ t, value, onChange }) {
  const [hasFocusWithin, setHasFocusWithin] = React.useState(false);
  const inputId = "manual-financial-employment-status";
  return (
    <div
      className="lender-financial-step__field"
      onFocusCapture={() => {
        setHasFocusWithin(true);
      }}
      onBlurCapture={(event) => {
        const nextFocusedElement = event.relatedTarget;
        if (!event.currentTarget.contains(nextFocusedElement)) {
          setHasFocusWithin(false);
        }
      }}
    >
      <FieldLabel
        label={t("fields.employmentStatus", "Employment status")}
        htmlFor={inputId}
        tooltip={t(
          "tooltips.employmentStatus",
          "This optional information is collected for lessons learned and policy analysis.",
        )}
        tooltipLabel={t("tooltips.moreInformation", "More information")}
        forceTooltipOpen={hasFocusWithin}
      />
      <select
        id={inputId}
        className="lender-financial-step__input lender-financial-step__select"
        value={value || ""}
        onChange={(event) => {
          onChange("employment_status", event.target.value);
        }}
      >
        <option value="">
          {t("placeholders.selectEmploymentStatus", "Select employment status")}
        </option>
        {EMPLOYMENT_STATUS_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {t(option.labelKey)}
          </option>
        ))}
      </select>
      <span className="lender-financial-step__optional-label">
        {t(
          "labels.optionalLessonsLearned",
          "Optional — collected for lessons learned",
        )}
      </span>
    </div>
  );
}
function RenovationBudgetField({ t, value, onChange, error }) {
  const [hasFocusWithin, setHasFocusWithin] = React.useState(false);
  const inputId = "manual-financial-available-renovation-budget";
  const errorId = `${inputId}-error`;
  return (
    <div
      className={`lender-financial-step__field ${
        error ? "lender-financial-step__field--error" : ""
      }`}
      onFocusCapture={() => {
        setHasFocusWithin(true);
      }}
      onBlurCapture={(event) => {
        const nextFocusedElement = event.relatedTarget;
        if (!event.currentTarget.contains(nextFocusedElement)) {
          setHasFocusWithin(false);
        }
      }}
    >
      <FieldLabel
        label={t(
          "fields.availableRenovationBudget",
          "Available renovation budget for OSS planning",
        )}
        htmlFor={inputId}
        required
        tooltip={t(
          "tooltips.availableRenovationBudget",
          "The financial envelope available for renovation works and used by the OSS when preparing the renovation plan.",
        )}
        tooltipLabel={t("tooltips.moreInformation", "More information")}
        forceTooltipOpen={hasFocusWithin}
      />
      <div className="lender-financial-step__money-control">
        <span className="lender-financial-step__currency" aria-hidden="true">
          €
        </span>
        <input
          id={inputId}
          className="lender-financial-step__input lender-financial-step__input--money"
          type="text"
          value={formatEuroInput(value)}
          onChange={(event) => {
            onChange(
              "available_renovation_budget",
              normalizeEuroInput(event.target.value),
            );
          }}
          placeholder={t("placeholders.amount", "0.00")}
          inputMode="decimal"
          autoComplete="off"
          required
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
        />
      </div>
      {error ? (
        <span
          id={errorId}
          className="lender-financial-step__error"
          role="alert"
        >
          {error}
        </span>
      ) : null}
    </div>
  );
}
export default function LenderManualLoanCaseFinancialInfo() {
  const { t } = useTranslation("lender-financial-info");
  const {
    loanCaseData,
    loanCaseId,
    currentStepKey,
    stepValues,
    handleStepValidityChange,
    handleStepValuesChange,
  } = useOutletContext();
  const personalStep = stepValues?.["personal-info"] || {};
  const storedPersonalInfo = personalStep.personal_info || personalStep;
  const primaryApplicant =
    storedPersonalInfo?.primary_applicant ||
    loanCaseData?.personal_info?.primary_applicant ||
    loanCaseData?.primary_applicant ||
    null;
  const personalStepHasCoApplicant =
    storedPersonalInfo &&
    typeof storedPersonalInfo === "object" &&
    Object.prototype.hasOwnProperty.call(storedPersonalInfo, "co_applicant");

  const coApplicant = personalStepHasCoApplicant
    ? storedPersonalInfo.co_applicant
    : (loanCaseData?.personal_info?.co_applicant ??
      loanCaseData?.co_applicant ??
      null);
  const hasCoApplicant = Boolean(coApplicant);
  const primaryApplicantName = [
    primaryApplicant?.first_name,
    primaryApplicant?.last_name,
  ]
    .filter(Boolean)
    .join(" ");
  const coApplicantName = [coApplicant?.first_name, coApplicant?.last_name]
    .filter(Boolean)
    .join(" ");
  const [financialInfo, setFinancialInfo] =
    React.useState(EMPTY_FINANCIAL_INFO);
  const initializedCaseRef = React.useRef(null);
  React.useEffect(() => {
    if (!loanCaseData || !loanCaseId) {
      return;
    }
    if (initializedCaseRef.current === String(loanCaseId)) {
      return;
    }
    const storedStep = stepValues?.[currentStepKey] || {};
    const storedFinancialInfo = storedStep.financial_info || storedStep;
    const backendFinancialInfo =
      loanCaseData.financial_info ||
      loanCaseData.dossier?.financial_info ||
      EMPTY_FINANCIAL_INFO;
    const hasStoredFinancialInfo =
      storedFinancialInfo &&
      typeof storedFinancialInfo === "object" &&
      Object.keys(storedFinancialInfo).length > 0;
    const source = hasStoredFinancialInfo
      ? storedFinancialInfo
      : backendFinancialInfo;
    setFinancialInfo(normalizeFinancialInfo(source, hasCoApplicant));
    initializedCaseRef.current = String(loanCaseId);
  }, [currentStepKey, hasCoApplicant, loanCaseData, loanCaseId, stepValues]);
  React.useEffect(() => {
    if (initializedCaseRef.current !== String(loanCaseId)) {
      return;
    }

    setFinancialInfo((previous) => {
      if (!hasCoApplicant) {
        if (previous.co_applicant === null) {
          return previous;
        }

        return {
          ...previous,
          co_applicant: null,
        };
      }

      if (previous.co_applicant) {
        return previous;
      }

      return {
        ...previous,
        co_applicant: {
          ...EMPTY_APPLICANT_FINANCIAL_INFO,
        },
      };
    });
  }, [hasCoApplicant, loanCaseId]);

  const handleApplicantChange = React.useCallback(
    (applicantKey, name, value) => {
      setFinancialInfo((previous) => ({
        ...previous,
        [applicantKey]: {
          ...(previous[applicantKey] || EMPTY_APPLICANT_FINANCIAL_INFO),
          [name]: value,
        },
      }));
    },
    [],
  );
  const handleApplicationChange = React.useCallback((name, value) => {
    setFinancialInfo((previous) => ({
      ...previous,
      [name]: value,
    }));
  }, []);
  const renovationBudget = toNumber(financialInfo.available_renovation_budget);
  const renovationBudgetIsValid =
    hasValue(financialInfo.available_renovation_budget) && renovationBudget > 0;
  const renovationBudgetError =
    hasValue(financialInfo.available_renovation_budget) && renovationBudget <= 0
      ? t(
          "validation.renovationBudgetPositive",
          "The available renovation budget must be greater than zero.",
        )
      : "";
  React.useEffect(() => {
    if (initializedCaseRef.current !== String(loanCaseId)) {
      return;
    }
    handleStepValidityChange(
      currentStepKey,
      renovationBudgetIsValid,
      renovationBudgetIsValid ? [] : [t("fields.availableRenovationBudget", "Available renovation budget")],
    );
    handleStepValuesChange(currentStepKey, {
      financial_info: {
        primary_applicant: {
          dossier_applicant_id: primaryApplicant?.id || null,
          employment_status:
            financialInfo.primary_applicant?.employment_status || "",
        },
        co_applicant:
          hasCoApplicant && financialInfo.co_applicant
            ? {
                dossier_applicant_id: coApplicant?.id || null,
                employment_status:
                  financialInfo.co_applicant.employment_status || "",
              }
            : null,
        available_renovation_budget: hasValue(
          financialInfo.available_renovation_budget,
        )
          ? sanitizeMoneyInput(financialInfo.available_renovation_budget)
          : null,
      },
    });
  }, [
    coApplicant?.id,
    currentStepKey,
    financialInfo,
    handleStepValidityChange,
    handleStepValuesChange,
    hasCoApplicant,
    loanCaseId,
    primaryApplicant?.id,
    renovationBudgetIsValid,
  ]);
  return (
    <div className="lender-financial-step">
      <div className="lender-financial-step__intro">
        <div className="lender-financial-step__intro-icon">
          <Landmark size={21} strokeWidth={2.1} />
        </div>
        <div className="lender-financial-step__intro-copy">
          <h2>{t("title", "Financial information")}</h2>
          <p>
            {t(
              "description",
              "Record employment information for each borrower and define the budget available for OSS renovation planning.",
            )}
          </p>
        </div>
      </div>
      <div className="lender-financial-step__stack">
        <section className="lender-financial-step__card">
          <div className="lender-financial-step__card-header">
            <div className="lender-financial-step__card-heading">
              <span className="lender-financial-step__card-icon">
                <BriefcaseBusiness size={18} strokeWidth={2.2} />
              </span>
              <div>
                <h3>
                  {t("cards.primaryEmployment", "Primary applicant employment")}
                </h3>
                <p>
                  {primaryApplicantName ||
                    t("labels.primaryApplicant", "Primary applicant")}
                </p>
              </div>
            </div>
          </div>
          <div className="lender-financial-step__fields">
            <EmploymentStatusField
              t={t}
              value={financialInfo.primary_applicant?.employment_status || ""}
              onChange={(name, value) =>
                handleApplicantChange("primary_applicant", name, value)
              }
            />
          </div>
        </section>
        {hasCoApplicant ? (
          <section className="lender-financial-step__card">
            <div className="lender-financial-step__card-header">
              <div className="lender-financial-step__card-heading">
                <span className="lender-financial-step__card-icon">
                  <BriefcaseBusiness size={18} strokeWidth={2.2} />
                </span>
                <div>
                  <h3>
                    {t("cards.coBorrowerEmployment", "Co-borrower employment")}
                  </h3>
                  <p>
                    {coApplicantName || t("labels.coBorrower", "Co-borrower")}
                  </p>
                </div>
              </div>
            </div>
            <div className="lender-financial-step__fields">
              <EmploymentStatusField
                t={t}
                value={financialInfo.co_applicant?.employment_status || ""}
                onChange={(name, value) =>
                  handleApplicantChange("co_applicant", name, value)
                }
              />
            </div>
          </section>
        ) : null}
        <section className="lender-financial-step__card lender-financial-step__card--budget">
          <div className="lender-financial-step__card-header">
            <div className="lender-financial-step__card-heading">
              <span className="lender-financial-step__card-icon">
                <Wrench size={18} strokeWidth={2.2} />
              </span>
              <div>
                <h3>{t("cards.renovationPlanning", "Renovation planning")}</h3>
                <p>
                  {t(
                    "cards.renovationPlanningDescription",
                    "Define the financial envelope that the OSS can use when preparing the renovation plan.",
                  )}
                </p>
              </div>
            </div>
          </div>
          <div className="lender-financial-step__fields">
            <RenovationBudgetField
              t={t}
              value={financialInfo.available_renovation_budget}
              onChange={handleApplicationChange}
              error={renovationBudgetError}
            />
          </div>
        </section>
      </div>
    </div>
  );
}
